// @flow
import { forkJoin, of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import {
  postReportApi, uploadAttachmentApi, endpoints, fetchReportMotivesAvailableApi,
  fetchReportMotivesPurchaserApi, fetchReportMotivesTravelerApi,
} from './api';
import { ofType } from 'redux-observable';
import { setNewAlert } from '../alert/duck';
import update from 'immutability-helper';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';

export type Report = {
  type: string;
  orderId?: string;
  motive?: string;
  message?: string;
  images?: {
    valueURI: string;
    name: string;
  }[];
  imageId?: string;
};

export type Motive = {
  type: 'report-motive';
  name: string;
  translations: { [string]: string };
};

export type ReportAction = {
  +type?: string;
  +reportType?: string;
  +report?: Report;
  +error?: HTTPError;
  +motives?: Motive[][];
};

export type MotiveKey = 'available' | 'purchaser' | 'traveler';

export type ReportState = {
  +isReporting?: boolean;
  +reportError?: HTTPError;
  +reportSuccess?: boolean;
  +isFetchingMotives?: boolean;
  +motives?: { [key: MotiveKey]: Motive[][] };
  +motivesError?: HTTPError;
};

// Actions
const REPORT_POST_REPORT_REQUEST = 'REPORT_POST_REPORT_REQUEST';
const REPORT_POST_REPORT_IMAGE_SUCCESS = 'REPORT_POST_REPORT_IMAGE_SUCCESS';
const REPORT_POST_REPORT_SUCCESS = 'REPORT_POST_REPORT_SUCCESS';
const REPORT_POST_REPORT_ERROR = 'REPORT_POST_REPORT_ERROR';
const REPORT_FETCH_MOTIVES_REQUEST = 'REPORT_FETCH_MOTIVES_REQUEST';
const REPORT_FETCH_MOTIVES_SUCCESS = 'REPORT_FETCH_MOTIVES_SUCCESS';
const REPORT_FETCH_MOTIVES_ERROR = 'REPORT_FETCH_MOTIVES_ERROR';
const REPORT_RESET_STATE = 'REPORT_RESET_STATE';

// Action creators
export function reportFunc(report: Report): ReportAction {
  return {
    type: REPORT_POST_REPORT_REQUEST,
    report,
  }
}

export function reportImageSuccessFunc(report: Report): ReportAction {
  return {
    type: REPORT_POST_REPORT_IMAGE_SUCCESS,
    report,
  }
}

export function reportSuccessFunc(): ReportAction {
  return {
    type: REPORT_POST_REPORT_SUCCESS,
  }
}

export function reportErrorFunc(error: HTTPError): ReportAction {
  return {
    type: REPORT_POST_REPORT_ERROR,
    error,
  }
}

export function fetchReportMotives(): ReportAction {
  return {
    type: REPORT_FETCH_MOTIVES_REQUEST,
  }
}

export function fetchReportMotivesSuccess(motives: Motive[][]): ReportAction {
  return {
    type: REPORT_FETCH_MOTIVES_SUCCESS,
    motives,
  }
}

export function fetchReportMotivesErrorFn(error: HTTPError): ReportAction {
  return {
    type: REPORT_FETCH_MOTIVES_ERROR,
    error,
  }
}

export function resetState(): ReportAction {
  return {
    type: REPORT_RESET_STATE,
  }
}

export const actions = {
  REPORT_POST_REPORT_REQUEST,
  REPORT_POST_REPORT_IMAGE_SUCCESS,
  REPORT_POST_REPORT_SUCCESS,
  REPORT_POST_REPORT_ERROR,
  REPORT_FETCH_MOTIVES_REQUEST,
  REPORT_FETCH_MOTIVES_SUCCESS,
  REPORT_FETCH_MOTIVES_ERROR,
  REPORT_RESET_STATE,
  reportFunc,
  reportImageSuccessFunc,
  reportSuccessFunc,
  reportErrorFunc,
  fetchReportMotives,
  fetchReportMotivesSuccess,
  fetchReportMotivesErrorFn,
  resetState,
};

export const initialState: ReportState = {};

// Reducer
export default function reducer(state: ReportState = initialState,
                                action: ReportAction): ReportState {
  switch (action.type) {
    case REPORT_POST_REPORT_REQUEST:
      return update(state, {
        isReporting: { $set: true },
        $unset: ['reportError'],
      });
    case REPORT_POST_REPORT_SUCCESS:
      return update(state, {
        reportSuccess: { $set: true },
        $unset: ['isReporting']
      });
    case REPORT_POST_REPORT_ERROR:
      return update(state, {
        reportError: { $set: action.error },
        $unset: ['isReporting']
      });
    case REPORT_FETCH_MOTIVES_REQUEST:
      return update(state, {
        isFetchingMotives: { $set: true },
        $unset: ['motivesError'],
      });
    case REPORT_FETCH_MOTIVES_SUCCESS:
      const up: { [key: string]: any } = {
        $unset: ['isFetchingMotives', 'motivesError'],
      };
      if (action.motives) {
        up.motives = {
          $set: {
            available: action.motives[0],
            purchaser: action.motives[1],
            traveler: action.motives[2],
          }
        };
      }
      return update(state, up);
    case REPORT_FETCH_MOTIVES_ERROR:
      return update(state, {
        motivesError: { $set: action.error },
        $unset: ['isFetchingMotives'],
      });
    case REPORT_RESET_STATE:
      return update(state, {
        $unset: ['isReporting', 'reportSuccess', 'reportError', 'isFetchingMotives'],
      });
    default:
      return state;
  }
}

// Epics
export function reportImageEpic(action$: Observable<ReportAction>): Observable<ReportAction> {
  return action$.pipe(
    ofType(REPORT_POST_REPORT_REQUEST),
    mergeMap(({ report }) => {
        const { images } = report;
        if (!!images) {
          const obs = images.map((image: { valueURI: string; name: string; }) => {
            const blob = new Blob([image.valueURI]);
            return uploadAttachmentApi(blob, {
              route: `${endpoints.POST_ATTACHMENT}?filename=${image.name}`,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          });
          return forkJoin(obs).pipe(
            mergeMap((attachmentsId: { imageId: string }[]) => of(
              reportImageSuccessFunc(update(report, {
                $unset: ['images'],
                attachments: { $set: attachmentsId.map(({ imageId }) => imageId) },
              }))
            )),
            catchError((e: HTTPError) => {
              if (Math.round(e.code / 100) === 5) {
                return of(reportErrorFunc(e),
                  setNewAlert(`${Date.now()}`, 'error', undefined, 'httpErrors.post.500'),
                );
              } else if (e.code === 400) {
                return of(reportErrorFunc(e),
                  setNewAlert(`${Date.now()}`, 'error', undefined, 'httpErrors.post.400'),
                );
              } else if (e.code === 413) {
                return of(reportErrorFunc(e),
                  setNewAlert(`${Date.now()}`, 'error', undefined, 'httpErrors.post.413'),
                );
              }
              return of(reportErrorFunc(e));
            }));
        } else {
          return postReportApi(report).pipe(
            map(() => reportSuccessFunc()),
            catchError((e: HTTPError) => of((reportErrorFunc(e)))));
        }
      }
    ));
}

export function reportEpic(action$: Observable<ReportAction>): Observable<ReportAction> {
  return action$.pipe(
    ofType(REPORT_POST_REPORT_IMAGE_SUCCESS),
    mergeMap(({ report }) => postReportApi(report).pipe(
      map(() => reportSuccessFunc()),
      catchError((e: HTTPError) => of((reportErrorFunc(e)))))
    ));
}

export function motivesEpic(action$: Observable<ReportAction>): Observable<ReportAction> {
  return action$.pipe(
    ofType(REPORT_FETCH_MOTIVES_REQUEST),
    mergeMap(() => {
        const obs = [
          fetchReportMotivesAvailableApi(),
          fetchReportMotivesPurchaserApi(),
          fetchReportMotivesTravelerApi(),
        ];
        return forkJoin(obs).pipe(
          map((motives: Motive[][]) => fetchReportMotivesSuccess(motives)),
          catchError((e: HTTPError) => of((fetchReportMotivesErrorFn(e)))));
      }
    ));
}



// WEBPACK FOOTER //
// ./src/modules/report/duck.js